import { _ } from "../../core/polyglot/polyglot.js";
import ComponentNode from "./ComponentNode.js";
import formulaFactory from "./formula/formulaFactory.js";
import keyFigureHighchartsOptions from "./rendering/keyFigureHighchartsOptions.js";
import keyFigureViewOptions from "./rendering/keyFigureViewOptions.js";
import NaFormulaNode from "./formula/NaFormulaNode.js";
import visibleKeyFigures from "./preset/visibleKeyFigures.js";

/**
 * `keyFigure` represents the root of a component tree.
 *
 * @param {string} [spec.formulaData] - String representation of the formula used to compute the keyFigure
 * @param {string} [spec.description] - Description of the keyFigure
 * @param {string} [spec.comment] - Comment about the keyFigure written by a consultant
 * @param {string} [spec.basId] - BASId of the keyFigure
 * @param {string} [spec.subtitle] - Short description of the key figure.
 * @param {{}} [spec.chartOptions] - Options used for plotting the keyFigure with
 * Highcharts. See `keyFigureHighchartsOptions.js` for system keyfigure chart
 * options.
 * @param {boolean} [spec.isNew] - Whether or not the keyfigure has been saved at
 * least once in the backend.
 * @param {boolean} [spec.isKnown] - Whether the receiver is one of
 * @param {string} [ownerId] - Identifier for the entity (company,
 * company type or country) owning the receiver.
 * @param {string} [ownerType] - Indetifies what type of entity that owns the keyfigure,
 *  ie. Country, Agency, Company...
 * the well-known (as defined in `SystemKeyFigureFactor `) key figures. When this
 * is false, the receiver is said to be a "custom key figure".
 */
export default class KeyFigure extends ComponentNode {
	constructor(spec = {}) {
		super(spec);
		this._formulaData = spec.formulaData;
		this._formula = spec.formula || new NaFormulaNode();
		this._description = spec.description || "";
		this._comment = spec.comment || "";
		this._basId = spec.basId || "";
		this._subtitle = spec.subtitle;
		this._isNew = spec.isNew === undefined ? true : spec.isNew;
		this._isKnown = spec.isKnown;
		this._knownName = spec.knownName;
		this._ownerId = spec.ownerId;
		this._ownerType = spec.ownerType;
		this._accountOperation = spec.accountOperation;

		this._chartOptions = spec.chartOptions || {
			...(this._isKnown
				? chartOptionsForKnownKeyFigure(this._id)
				: chartOptionsForCustomKeyFigure()),
		};
	}

	getKnownName() {
		return this._knownName;
	}

	getComment() {
		return this._comment;
	}

	setComment(comment) {
		this._comment = comment;
	}

	getBasId() {
		return this._basId;
	}

	getDescription() {
		return this._description;
	}

	setDescription(description) {
		this._description = description;
	}

	getIdentifier() {
		return this.getBasId() || this.getId();
	}

	getGroup() {
		if (this.isCustom()) {
			return _("Custom");
		}

		const data = keyFigureViewOptions[this._id];
		return (data && data.group) || _("Other");
	}

	getSubtitle() {
		if (this._subtitle) {
			return this._subtitle;
		}

		const data = keyFigureViewOptions[this._id];
		return (data && data.subtitle) || "";
	}

	setSubtitle(subtitle) {
		this._subtitle = subtitle;
	}

	getChartOptions() {
		return this._chartOptions;
	}

	getChartColorOption() {
		return this._chartOptions.color;
	}

	setChartColorOption(color) {
		this._chartOptions.color = color;
	}

	setChartSeriesTypeOptions({ type, dashStyle }) {
		this._chartOptions.type = type;
		this._chartOptions.dashStyle = dashStyle;
	}

	/**
	 * If the receiver contains "raw" formulaData, build the formula lazily from
	 * the data.
	 *
	 * The formula is build lazily to avoid dependency issues with formula nodes
	 * depending on key figures this may not yet be loaded.
	 */
	getFormula(options = {}) {
		if (this._formulaData && this._formulaData.expression) {
			const { additionalKeyFigures, ...otherOptions } = options;
			const keyFigures = {
				...this._formulaData.keyFigures,
				...additionalKeyFigures,
			};

			this._formula = formulaFactory.materialize(
				this._formulaData.expression,
				{
					getKeyFigureFromCache:
						this._formulaData.getKeyFigureFromCache,
					keyFigures,
					mainKeyFigure: this,
					...otherOptions,
				},
			);

			// Make sure we don't build the formula next time this.getFormula()
			// is called:
			this._formulaData = null;
		}

		return this._formula;
	}

	setFormula(formula) {
		// nullify formulaData, or getFormula might return something different
		this._formulaData = null;
		this._formula = formula;
	}

	getPrecision() {
		return this._precision;
	}

	/**
	 * Defines the level of accuracy of the keyfigure value.
	 *
	 * @param {string} precision - can be "default" (standard accuracy
	 * set by the local country settings), "decimals" (to force using
	 * a float), "no-decimals" (to prevent using a float), or
	 * "shorten-thousands" (to force using the suffix `k`).
	 */
	setPrecision(precision) {
		this._precision = precision;
	}

	getOperationType() {
		return this._accountOperation?.getOperationType();
	}

	hasSpecificPrecision() {
		return this._precision && this._precision !== "default";
	}

	isKeyFigure() {
		return true;
	}

	isCustom() {
		return !this.isKnown();
	}

	isRenamed() {
		return this._name && this._knownName && this._name !== this._knownName;
	}

	isKnown() {
		return this._isKnown;
	}

	hasBeenModified() {
		if (this.isCustom()) return true;
		if (this.isRenamed()) return true;
		return this._hasOwner();
	}

	isNonFinancialKeyFigure() {
		return !this.isFinancialKeyFigure();
	}

	/**
	 * A financial key figure is one whose formula is not a
	 * FormulaDataNode.
	 */
	isFinancialKeyFigure() {
		// Avoid materializing the formula just for the sake of
		// checking the type of the formula node. This is important
		// because materializing a formula will crash if the KPI
		// references non-existing KPIs.
		if (this._formulaData) {
			return !formulaFactory.isKeyFigureDataNodeType(
				this._formulaData.expression.type,
			);
		}
		return !this.getFormula().isKeyFigureDataNode();
	}

	isVisible() {
		return visibleKeyFigures.isKeyFigureVisible(this);
	}

	isNew() {
		return this._isNew;
	}

	validate({ collector }) {
		collector.addErrorIf(!this._name, _("Keyfigure name missing"));
	}

	canBeDeletedBy(owner) {
		return !this.isKnown() && this.belongsTo(owner);
	}

	canBeDownloadedBy(owner) {
		return !this.isKnown() && this.belongsTo(owner);
	}

	canBeUploadedBy(owner) {
		return !this.isKnown() && this.belongsTo(owner);
	}

	canBeRestoredFor(owner) {
		return this.isKnown() && this.belongsTo(owner);
	}

	belongsTo(owner) {
		return this._ownerId === owner.getLegacyId();
	}

	clone() {
		return new this.constructor({
			name: this.getName(),
			description: this._description,
			formula: this._formula,
			formulaData: this._formulaData,
			subtitle: this._subtitle,
			comment: this._comment,
			isKnown: this._isKnown,
			knownName: this._knownName,
			ownerId: this._ownerId,
			ownerType: this._ownerType,
			valueType: this._valueType,
			numberOfDecimals: this._numberOfDecimals,
		});
	}

	toLabel() {
		return this.getName();
	}

	_hasOwner() {
		return !!this._ownerId;
	}

	_getMatchingFields() {
		return [this.getName(), this.getBasId(), this.getId()];
	}

	_serializedProperties() {
		const properties = super._serializedProperties();

		properties.subtitle = this._subtitle;
		properties.description = this._description;
		properties.chartOptions = this._chartOptions;
		properties.expression = this.getFormula();

		return properties;
	}

	static valueTypes() {
		return [
			{ label: _("Amount"), value: "Amount" },
			{ label: _("Percent"), value: "Percent" },
			{ label: _("Ratio"), value: "Ratio" },
			{ label: _("Count"), value: "Count" },
		];
	}

	static transformMaterializationData(data, context) {
		data.formulaData = {
			expression: data.expression,
			getKeyFigureFromCache: context.getKeyFigureFromCache,
			keyFigures: context.keyFigures,
		};

		data.isNew = false;
	}

	static getAllVisible() {
		return visibleKeyFigures.getKeyFigures();
	}

	static getType() {
		return "KeyFigure";
	}
}

function chartOptionsForCustomKeyFigure() {
	return {
		color: "black",
		type: "line",
	};
}

function chartOptionsForKnownKeyFigure(id) {
	const defaultChartColor = "#89B1CD";
	const defaultOptions = { color: defaultChartColor };
	const options = keyFigureHighchartsOptions[id];

	return options || defaultOptions;
}
